import { toastError, toastSuccess } from 'admination-react'
import { ApiError, captureWriteIdentity, isWriteIdentityCurrent, RequestTimeoutError, type WriteIdentity } from '@/core/api/http'
import {
  isZuweisungslisteResult,
  uploadZuweisungsliste,
  zuweisungslisteErrorBody,
  type ZuweisungslisteErrorBody,
  type ZuweisungslisteResult,
} from '../../api/zuweisungsliste'
import { composeMassnahmeSelection } from '@/core/lib/massnahme'

export function zuweisungslisteSuccessMessage(result: ZuweisungslisteResult): string {
  return [
    `${result.angelegt} Teilnehmende angelegt`,
    result.uebersprungen.length > 0 && `${result.uebersprungen.length} bereits vorhanden, übersprungen`,
    result.massnahme && `Maßnahme ${composeMassnahmeSelection(result.massnahme.nummer, result.massnahme.bezeichnung)}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function zuweisungslisteErgebnisZeigen(result: ZuweisungslisteResult): boolean {
  return result.uebersprungen.length > 0 || result.massnahme === null
}

export function zuweisungslisteFailureMessage(error: unknown): string {
  const body = zuweisungslisteErrorBody(error)
  if (body) return body.meldung
  if (error instanceof ApiError) {
    return `Hochladen fehlgeschlagen (Fehler ${error.status}). Bitte später erneut versuchen.`
  }
  if (error instanceof RequestTimeoutError) return error.message
  return "Hochladen fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut."
}

export type ZuweisungslisteUploadDeps = {
  reloadDashboard: () => void
  onBusyChange: (busy: boolean) => void
  onFehler: (body: ZuweisungslisteErrorBody) => void
  onErgebnis: (result: ZuweisungslisteResult) => void
}

function reportUnreadable2xx(deps: ZuweisungslisteUploadDeps): void {
  deps.reloadDashboard()
  toastError("Die Antwort des Servers war unvollständig. Bitte prüfen Sie das Board.")
}

let uploadInFlight: WriteIdentity | null = null

export async function runZuweisungslisteUpload(file: File, deps: ZuweisungslisteUploadDeps): Promise<void> {
  if (uploadInFlight && isWriteIdentityCurrent(uploadInFlight)) return
  const identity = captureWriteIdentity()
  uploadInFlight = identity
  try {
    deps.onBusyChange(true)
    try {
      const result = await uploadZuweisungsliste(file)
      if (!isWriteIdentityCurrent(identity)) return
      if (!isZuweisungslisteResult(result)) {
        reportUnreadable2xx(deps)
        return
      }
      toastSuccess(zuweisungslisteSuccessMessage(result))
      if (zuweisungslisteErgebnisZeigen(result)) deps.onErgebnis(result)
      deps.reloadDashboard()
    } catch (e) {
      if (!isWriteIdentityCurrent(identity)) return
      if (e instanceof SyntaxError) {
        reportUnreadable2xx(deps)
        return
      }
      if (e instanceof ApiError && e.status === 422) {
        const body = zuweisungslisteErrorBody(e)
        if (body) {
          deps.onFehler(body)
          return
        }
      }
      toastError(zuweisungslisteFailureMessage(e))
    } finally {
      if (isWriteIdentityCurrent(identity)) deps.onBusyChange(false)
    }
  } finally {
    if (uploadInFlight === identity) uploadInFlight = null
  }
}
